/**
 * ImageBitmap 缓存工具
 *
 * 使用 ImageBitmap 替代 Canvas 作为绘制源可以显著提升 drawImage 性能。
 * ImageBitmap 是浏览器原生优化的位图对象，绘制速度比 Canvas 快 2-3 倍。
 */

// 全局 ImageBitmap 缓存，与 canvas 缓存分开管理
const bitmapCache = new WeakMap<HTMLCanvasElement, ImageBitmap>();

// 正在创建中的 bitmap Promise，避免重复创建
const pendingBitmaps = new WeakMap<HTMLCanvasElement, Promise<ImageBitmap>>();

/**
 * 检查浏览器是否支持 createImageBitmap
 */
export const supportsImageBitmap = typeof createImageBitmap === 'function';

/**
 * 从 Canvas 创建 ImageBitmap（异步）
 * 如果已有缓存则直接返回，否则创建新的
 */
export async function createBitmapFromCanvas(
  canvas: HTMLCanvasElement,
  sx: number = 0,
  sy: number = 0,
  sw?: number,
  sh?: number
): Promise<ImageBitmap | null> {
  if (!supportsImageBitmap) {
    return null;
  }

  // 检查是否已有缓存
  const cached = bitmapCache.get(canvas);
  if (cached) {
    return cached;
  }

  // 检查是否正在创建中
  const pending = pendingBitmaps.get(canvas);
  if (pending) {
    return pending;
  }

  // 创建新的 ImageBitmap
  const width = sw ?? canvas.width;
  const height = sh ?? canvas.height;

  if (width <= 0 || height <= 0) {
    return null;
  }

  try {
    const promise = createImageBitmap(canvas, sx, sy, width, height);
    pendingBitmaps.set(canvas, promise);

    const bitmap = await promise;
    bitmapCache.set(canvas, bitmap);
    pendingBitmaps.delete(canvas);

    return bitmap;
  } catch {
    pendingBitmaps.delete(canvas);
    // 静默失败，回退到使用 canvas
    return null;
  }
}

/**
 * 同步获取已缓存的 ImageBitmap
 * 如果没有缓存则返回 null
 */
export function getCachedBitmap(canvas: HTMLCanvasElement): ImageBitmap | null {
  return bitmapCache.get(canvas) || null;
}

/**
 * 释放 Canvas 关联的 ImageBitmap
 */
export function releaseBitmap(canvas: HTMLCanvasElement): void {
  const bitmap = bitmapCache.get(canvas);
  if (bitmap) {
    bitmap.close();
    bitmapCache.delete(canvas);
  }
  pendingBitmaps.delete(canvas);
}

/**
 * 异步创建 ImageBitmap 并缓存到 cache entry
 * 这个函数会在后台创建 bitmap，不阻塞当前渲染
 */
export function scheduleBitmapCreation(
  canvas: HTMLCanvasElement,
  actualWidth: number,
  actualHeight: number,
  onComplete?: (bitmap: ImageBitmap) => void
): void {
  if (!supportsImageBitmap) {
    return;
  }

  // 如果已有缓存，直接回调
  const cached = bitmapCache.get(canvas);
  if (cached) {
    onComplete?.(cached);
    return;
  }

  // 异步创建
  createBitmapFromCanvas(canvas, 0, 0, actualWidth, actualHeight).then((bitmap) => {
    if (bitmap) {
      onComplete?.(bitmap);
    }
  });
}

/**
 * 绘制 ImageBitmap 或 Canvas 到目标 context
 * 优先使用 ImageBitmap，如果没有则使用 Canvas
 */
export function drawBitmapOrCanvas(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  bitmap: ImageBitmap | null | undefined,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  const source = bitmap || canvas;
  ctx.drawImage(source, sx, sy, sw, sh, dx, dy, dw, dh);
}

/**
 * 缓存条目类型扩展
 * 用于在现有缓存结构中添加 bitmap 支持
 */
export interface BitmapCacheExtension {
  bitmap?: ImageBitmap | null;
  bitmapPending?: boolean;
}

/**
 * 为缓存条目创建 bitmap（非阻塞）
 * 在首次渲染后调用，下次渲染时即可使用 bitmap
 */
export function ensureBitmapForCache<
  T extends BitmapCacheExtension & { canvas: HTMLCanvasElement },
>(cache: T, actualWidth: number, actualHeight: number): void {
  if (!supportsImageBitmap) {
    return;
  }

  // 已有 bitmap 或正在创建中
  if (cache.bitmap || cache.bitmapPending) {
    return;
  }

  cache.bitmapPending = true;

  scheduleBitmapCreation(cache.canvas, actualWidth, actualHeight, (bitmap) => {
    cache.bitmap = bitmap;
    cache.bitmapPending = false;
  });
}

/**
 * 获取缓存的最佳绘制源（bitmap 优先）
 */
export function getBestDrawSource<T extends BitmapCacheExtension & { canvas: HTMLCanvasElement }>(
  cache: T
): ImageBitmap | HTMLCanvasElement {
  return cache.bitmap || cache.canvas;
}

/**
 * 释放缓存条目的 bitmap
 */
export function disposeBitmapFromCache<
  T extends BitmapCacheExtension & { canvas: HTMLCanvasElement },
>(cache: T): void {
  if (cache.bitmap) {
    cache.bitmap.close();
    cache.bitmap = null;
  }
  cache.bitmapPending = false;
  releaseBitmap(cache.canvas);
}
